import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AdminIPAssets from "@/components/AdminIPAssets";
import AdminRenewalDigest from "@/components/AdminRenewalDigest";
import AdminAdvisoryBlast from "@/components/AdminAdvisoryBlast";
import AdminEngagementLetter from "@/components/AdminEngagementLetter";
import AdminStationery from "@/components/AdminStationery";
import { StarSpinner } from "@/components/StarSpinner";

const STATUSES = ["new", "in_review", "contacted", "engaged", "closed"];

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("intake");
  const [intakes, setIntakes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [i, c, s] = await Promise.all([
        api.get("/intake"),
        api.get("/contact"),
        api.get("/subscribers"),
      ]);
      setIntakes(i.data);
      setContacts(c.data);
      setSubscribers(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/intake/${id}`, { status });
      setIntakes((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success("Status updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const removeSubscriber = async (id) => {
    try {
      await api.delete(`/subscribers/${id}`);
      setSubscribers((prev) => prev.filter((s) => s.id !== id));
      toast.success("Removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const exportSubscribers = () => {
    const csv = ["email,source,subscribed_at", ...subscribers.map(s => `${s.email},${s.source || ""},${s.created_at}`)].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lonestar-subscribers-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const counts = {
    intake: intakes.length,
    new: intakes.filter((i) => i.status === "new").length,
    contact: contacts.length,
    subscribers: subscribers.length,
  };

  return (
    <div className="bg-ivory min-h-screen pt-32 pb-32" data-testid="admin-page">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-8 hairline-bottom pb-10">
          <div>
            <div className="kicker">Firm Administration</div>
            <h1 className="mt-4 font-serif text-5xl md:text-7xl leading-[0.98]">
              The Chambers,<br/>
              <span className="italic text-oxblood">{user?.name?.split(" ")[0] || "Admin"}.</span>
            </h1>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right text-xs uppercase tracking-[0.2em] text-slate2">
              <div>{counts.intake} intakes · {counts.new} new</div>
              <div>{counts.contact} correspondence</div>
            </div>
            <button onClick={logout} className="btn-ghost" data-testid="admin-logout">Sign Out</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-10 flex gap-8 hairline-bottom pb-4" data-testid="admin-tabs">
          <button
            onClick={() => { setTab("intake"); setSelected(null); }}
            className={`nav-link !text-base ${tab === "intake" ? "active text-oxblood" : ""}`}
            data-testid="admin-tab-intake"
          >
            Intake Inquiries · {counts.intake}
          </button>
          <button
            onClick={() => { setTab("contact"); setSelected(null); }}
            className={`nav-link !text-base ${tab === "contact" ? "active text-oxblood" : ""}`}
            data-testid="admin-tab-contact"
          >
            Correspondence · {counts.contact}
          </button>
          <button
            onClick={() => { setTab("subscribers"); setSelected(null); }}
            className={`nav-link !text-base ${tab === "subscribers" ? "active text-oxblood" : ""}`}
            data-testid="admin-tab-subscribers"
          >
            Journal Subscribers · {counts.subscribers}
          </button>
          <button
            onClick={() => { setTab("ip"); setSelected(null); }}
            className={`nav-link !text-base ${tab === "ip" ? "active text-oxblood" : ""}`}
            data-testid="admin-tab-ip"
          >
            IP Assets
          </button>
          <button
            onClick={() => { setTab("digest"); setSelected(null); }}
            className={`nav-link !text-base ${tab === "digest" ? "active text-oxblood" : ""}`}
            data-testid="admin-tab-digest"
          >
            Renewal Digest
          </button>
          <button
            onClick={() => { setTab("advisory"); setSelected(null); }}
            className={`nav-link !text-base ${tab === "advisory" ? "active text-oxblood" : ""}`}
            data-testid="admin-tab-advisory"
          >
            Client Advisory
          </button>
          <button
            onClick={() => { setTab("engagement"); setSelected(null); }}
            className={`nav-link !text-base ${tab === "engagement" ? "active text-oxblood" : ""}`}
            data-testid="admin-tab-engagement"
          >
            Engagement Letter
          </button>
          <button
            onClick={() => { setTab("stationery"); setSelected(null); }}
            className={`nav-link !text-base ${tab === "stationery" ? "active text-oxblood" : ""}`}
            data-testid="admin-tab-stationery"
          >
            Stationery
          </button>
        </div>

        {loading && (
          <div className="mt-16" data-testid="admin-loading">
            <StarSpinner label="Loading the docket" />
          </div>
        )}

        {/* Intake table */}
        {!loading && tab === "intake" && (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className={`${selected ? "lg:col-span-7" : "lg:col-span-12"}`}>
              {intakes.length === 0 ? (
                <div className="p-12 border border-transparent bg-linen/40 text-center font-serif italic text-slate2" data-testid="admin-intake-empty">
                  No inquiries yet.
                </div>
              ) : (
                <div className="bg-brass/30 grid grid-cols-1 gap-px" data-testid="admin-intake-list">
                  <div className="bg-linen/60 grid grid-cols-12 gap-4 px-6 py-4 text-xs uppercase tracking-[0.2em] text-slate2 font-medium">
                    <div className="col-span-3">Principal</div>
                    <div className="col-span-3">Practice · Stage</div>
                    <div className="col-span-3">Submitted</div>
                    <div className="col-span-3 text-right">Status</div>
                  </div>
                  {intakes.map((it) => {
                    const isSel = selected?.id === it.id;
                    return (
                      <button
                        key={it.id}
                        onClick={() => setSelected(it)}
                        data-testid={`admin-intake-${it.id}`}
                        className={`bg-ivory grid grid-cols-12 gap-4 px-6 py-5 text-left hover:bg-linen transition ${isSel ? "ring-1 ring-oxblood" : ""}`}
                      >
                        <div className="col-span-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-serif text-lg text-navy">{it.full_name}</div>
                            {it.matter_class === "private_client" && (
                              <span
                                className="inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.22em] bg-brass/15 text-brass font-medium"
                                data-testid={`admin-badge-private-${it.id}`}
                              >
                                Private Client
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate2">{it.email}</div>
                        </div>
                        <div className="col-span-3 text-sm">
                          <div className="font-medium">{it.practice_area}</div>
                          <div className="font-serif italic text-slate2">{it.stage || "—"}</div>
                        </div>
                        <div className="col-span-3 text-sm text-slate2 font-light">
                          {new Date(it.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                          <div className="text-xs text-slate2/70">{new Date(it.created_at).toLocaleTimeString()}</div>
                        </div>
                        <div className="col-span-3 text-right">
                          <span className="inline-block px-3 py-1 border border-transparent text-xs uppercase tracking-[0.2em] text-oxblood">
                            {it.status.replace("_", " ")}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {selected && (
              <aside className="lg:col-span-5 bg-linen/40 border border-transparent p-8 md:p-10 lg:sticky lg:top-32 h-fit" data-testid="admin-detail">
                <div className="flex items-center justify-between">
                  <div className="kicker">Inquiry</div>
                  <button onClick={() => setSelected(null)} className="text-slate2 text-sm" data-testid="admin-detail-close">close ×</button>
                </div>
                <h2 className="mt-4 font-serif text-3xl text-navy">{selected.full_name}</h2>
                {selected.matter_class === "private_client" && (
                  <div
                    className="mt-2 inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.22em] bg-brass/15 text-brass font-medium"
                    data-testid="admin-detail-private-badge"
                  >
                    Private Client
                  </div>
                )}
                <div className="font-serif italic text-oxblood">{selected.practice_area} · {selected.stage}</div>

                <dl className="mt-8 space-y-4 text-sm">
                  <DetailRow label="Email" value={selected.email} />
                  <DetailRow label="Phone" value={selected.phone || "—"} />
                  <DetailRow label="Location" value={selected.location || "—"} />
                  <DetailRow label="Entity" value={selected.entity_type || "—"} />
                  <DetailRow label="Value" value={selected.estimated_value || "—"} />
                  <DetailRow label="Timeline" value={selected.timeline || "—"} />
                  <DetailRow label="Preferred Contact" value={selected.preferred_contact || "—"} />
                  {selected.matter_class === "private_client" && (
                    <>
                      <DetailRow label="Role" value={selected.role || "—"} />
                      <DetailRow label="Referral" value={selected.referral_source || "—"} />
                    </>
                  )}
                </dl>

                <div className="mt-8 hairline pt-6">
                  <div className="kicker">Message</div>
                  <p className="mt-3 font-serif italic text-ink leading-relaxed">"{selected.message}"</p>
                </div>

                <div className="mt-8 hairline pt-6">
                  <div className="kicker">Set Status</div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        onClick={() => updateStatus(selected.id, s)}
                        data-testid={`admin-status-${s}`}
                        className={`px-3 py-2 text-xs uppercase tracking-[0.2em] border transition ${
                          selected.status === s
                            ? "bg-oxblood text-ivory border-oxblood"
                            : "bg-ivory border-transparent hover:border-oxblood"
                        }`}
                      >
                        {s.replace("_", " ")}
                      </button>
                    ))}
                  </div>
                </div>
              </aside>
            )}
          </div>
        )}

        {/* Contact table */}
        {!loading && tab === "contact" && (
          <div className="mt-10">
            {contacts.length === 0 ? (
              <div className="p-12 border border-transparent bg-linen/40 text-center font-serif italic text-slate2" data-testid="admin-contact-empty">
                No correspondence yet.
              </div>
            ) : (
              <div className="bg-brass/30 grid grid-cols-1 gap-px" data-testid="admin-contact-list">
                {contacts.map((c) => (
                  <article key={c.id} className="bg-ivory p-6 md:p-8" data-testid={`admin-contact-${c.id}`}>
                    <div className="flex flex-col md:flex-row md:items-baseline justify-between gap-4">
                      <div>
                        <div className="font-serif text-2xl text-navy">{c.name}</div>
                        <div className="text-xs text-slate2">{c.email}</div>
                      </div>
                      <div className="text-xs uppercase tracking-[0.2em] text-slate2">
                        {new Date(c.created_at).toLocaleString()}
                      </div>
                    </div>
                    {c.subject && <div className="mt-3 font-serif italic text-oxblood">Re: {c.subject}</div>}
                    <p className="mt-4 text-ink leading-relaxed font-serif">"{c.message}"</p>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subscribers table */}
        {!loading && tab === "subscribers" && (
          <div className="mt-10">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <p className="text-slate2 font-light">
                {subscribers.length} {subscribers.length === 1 ? "subscriber" : "subscribers"} to the Journal.
              </p>
              <button
                onClick={exportSubscribers}
                disabled={subscribers.length === 0}
                className="btn-ghost disabled:opacity-40"
                data-testid="admin-subscribers-export"
              >
                Export CSV
              </button>
            </div>
            {subscribers.length === 0 ? (
              <div className="p-12 border border-transparent bg-linen/40 text-center font-serif italic text-slate2" data-testid="admin-subscribers-empty">
                No Journal subscribers yet.
              </div>
            ) : (
              <div className="bg-brass/30 grid grid-cols-1 gap-px" data-testid="admin-subscribers-list">
                <div className="bg-linen/60 grid grid-cols-12 gap-4 px-6 py-4 text-xs uppercase tracking-[0.2em] text-slate2 font-medium">
                  <div className="col-span-6">Email</div>
                  <div className="col-span-3">Source</div>
                  <div className="col-span-2">Subscribed</div>
                  <div className="col-span-1 text-right"></div>
                </div>
                {subscribers.map((s) => (
                  <div key={s.id} className="bg-ivory grid grid-cols-12 gap-4 px-6 py-4 items-center" data-testid={`admin-subscriber-${s.id}`}>
                    <div className="col-span-6 font-serif text-lg text-navy break-all">{s.email}</div>
                    <div className="col-span-3 text-sm text-slate2 font-light">{s.source || "—"}</div>
                    <div className="col-span-2 text-xs text-slate2 uppercase tracking-[0.2em]">
                      {new Date(s.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                    </div>
                    <div className="col-span-1 text-right">
                      <button
                        onClick={() => removeSubscriber(s.id)}
                        className="text-slate2/60 hover:text-oxblood text-sm transition"
                        data-testid={`admin-subscriber-remove-${s.id}`}
                        aria-label="Remove subscriber"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* IP Assets */}
        {!loading && tab === "ip" && <AdminIPAssets />}

        {/* Renewal Digest */}
        {!loading && tab === "digest" && <AdminRenewalDigest />}

        {/* Client Advisory Blast */}
        {!loading && tab === "advisory" && <AdminAdvisoryBlast />}

        {/* Engagement Letter */}
        {!loading && tab === "engagement" && <AdminEngagementLetter />}

        {/* Stationery */}
        {!loading && tab === "stationery" && <AdminStationery onGoTo={setTab} />}
      </section>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 border-b-0 pb-3">
      <dt className="kicker text-[0.62rem]">{label}</dt>
      <dd className="col-span-2 font-serif text-slate2">{value}</dd>
    </div>
  );
}
